import { useEffect, useState } from "react";
import { translate } from "@/i18n";
import { hooksMessages } from "@/i18n/messages/hooks";
import {
  listInputTargetConversations,
  type InputTargetConversation,
} from "@/api/conversation-targets";
import { captureDesktopChatAuthScopeGuard } from "@/desktop/desktop-chat-auth-scope";
import { useProjectStore } from "@/stores/project-store";

const RECENT_INPUT_TARGET_LIMIT = 5;
const SEARCH_INPUT_TARGET_LIMIT = 8;
const INPUT_TARGET_SEARCH_DEBOUNCE_MS = 300;
const MIN_SEARCH_LENGTH = 1;
/** The backend rejects a longer picker search with a 400. */
const MAX_SEARCH_LENGTH = 100;

/** Shown by both the ring menu's Recent list and the `@` popup on a load error. */
export function inputTargetsErrorMessage(): string {
  return translate(hooksMessages, "inputTargetsLoadFailed");
}

export interface InputTargets {
  status: "idle" | "loading" | "ready" | "error";
  /**
   * "recent" without search text, "results" as soon as text is entered.
   * Reflects the rows currently shown, not the in-flight request, so a
   * "Results" header never sits above stale recent rows while a debounce
   * or request is pending.
   */
  mode: "recent" | "results";
  rows: InputTargetConversation[];
}

const IDLE: InputTargets = { status: "idle", mode: "recent", rows: [] };

function searchText(search: string): string {
  const normalized = search
    .trim()
    .replace(/\s+/g, " ")
    .slice(0, MAX_SEARCH_LENGTH);
  return normalized.length >= MIN_SEARCH_LENGTH ? normalized : "";
}

/**
 * Recent chats and sub-agents, or a debounced picker search from one
 * character. A response is dropped once a newer search, project or auth
 * scope replaced it, or the hook unmounted or was disabled meanwhile.
 */
export function useInputTargets({
  enabled,
  search,
}: {
  enabled: boolean;
  search: string;
}): InputTargets {
  const currentProjectId = useProjectStore((state) => state.currentProject?.id);
  const text = searchText(search);
  const [state, setState] = useState<InputTargets>(IDLE);
  useEffect(() => {
    if (!enabled) {
      setState(IDLE);
      return;
    }
    const requestedMode: InputTargets["mode"] = text ? "results" : "recent";
    let active = true;
    const isCurrentScope = captureDesktopChatAuthScopeGuard();
    setState((previous) => ({
      status: "loading",
      rows: previous.rows,
      // Only the first-ever load (no rows shown yet) adopts the requested
      // mode ahead of time; once rows are on screen, the mode sticks until
      // a response actually arrives.
      mode: previous.rows.length === 0 ? requestedMode : previous.mode,
    }));
    const load = () => {
      listInputTargetConversations({
        currentProjectId,
        ...(text
          ? { limit: SEARCH_INPUT_TARGET_LIMIT, search: text }
          : { limit: RECENT_INPUT_TARGET_LIMIT }),
      }).then(
        (rows) => {
          if (active && isCurrentScope()) {
            setState({ status: "ready", rows, mode: requestedMode });
          }
        },
        () => {
          if (active && isCurrentScope()) {
            setState((previous) => ({ ...previous, status: "error" }));
          }
        },
      );
    };
    const timer = text
      ? window.setTimeout(load, INPUT_TARGET_SEARCH_DEBOUNCE_MS)
      : null;
    if (timer === null) load();
    return () => {
      active = false;
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [currentProjectId, enabled, text]);
  return state;
}
